import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserCog, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, LoadingBlock, EmptyState } from "@/components/Common";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { getWallets } from "@/data/site";
import { useI18n, useL } from "@/context/I18nContext";
import api, { formatApiError } from "@/lib/api";

export default function AdminSpecialists() {
  const L = useL();
  const [items, setItems] = useState(null);
  const load = () => api.get("/admin/specialists").then(({ data }) => setItems(data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-2xl font-medium text-white">{L("Specialisti", "Specialists", "Spezialisten")}</h2>
        <CreateSpecialist onDone={load} />
      </div>
      {items === null ? <LoadingBlock /> : items.length === 0 ? <EmptyState icon={UserCog} title={L("Nessuno specialista", "No specialists", "Keine Spezialisten")} /> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((s) => (
            <Card key={s.id} className="p-5" data-testid={`specialist-${s.id}`}>
              <div className="flex items-center gap-3">
                <span className="h-10 w-10 rounded-full bg-cyanx/15 text-cyanx flex items-center justify-center font-semibold">{s.first_name?.[0]}{s.last_name?.[0]}</span>
                <div>
                  <p className="text-white font-medium">{s.first_name} {s.last_name}</p>
                  <p className="text-xs text-slate-500">{s.email}</p>
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between text-sm">
                <span className="text-slate-400">{L("Pratiche attive", "Active cases", "Aktive Fälle")}</span>
                <span className="text-cyanx font-medium">{s.active_cases}</span>
              </div>
              {s.wallet_types?.length > 0 && <div className="mt-2 flex flex-wrap gap-1.5">{s.wallet_types.map((w) => <span key={w} className="text-[10px] rounded-full border border-white/10 px-2 py-0.5 text-slate-400">{w}</span>)}</div>}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function CreateSpecialist({ onDone }) {
  const L = useL();
  const { lang } = useI18n();
  const wallets = getWallets(lang);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", password: "", wallet_types: [] });
  const toggleWallet = (w) => setF((s) => ({ ...s, wallet_types: s.wallet_types.includes(w) ? s.wallet_types.filter((x) => x !== w) : [...s.wallet_types, w] }));
  const submit = async () => {
    try { await api.post("/admin/specialists", f); toast.success(L("Specialista creato", "Specialist created", "Spezialist erstellt")); setOpen(false); setF({ first_name: "", last_name: "", email: "", password: "", wallet_types: [] }); onDone(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="bg-gold text-navy hover:bg-gold-hover" data-testid="create-specialist-btn"><Plus className="h-4 w-4 mr-1.5" />{L("Nuovo specialista", "New specialist", "Neuer Spezialist")}</Button></DialogTrigger>
      <DialogContent className="bg-navy-paper border-white/10">
        <DialogHeader><DialogTitle>{L("Crea specialista", "Create specialist", "Spezialist erstellen")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-slate-300">{L("Nome", "First name", "Vorname")}</Label><Input value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} className="mt-1.5 bg-navy border-white/10" data-testid="spec-first-name" /></div>
            <div><Label className="text-slate-300">{L("Cognome", "Last name", "Nachname")}</Label><Input value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} className="mt-1.5 bg-navy border-white/10" data-testid="spec-last-name" /></div>
          </div>
          <div><Label className="text-slate-300">{L("Email", "Email", "E-Mail")}</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="mt-1.5 bg-navy border-white/10" data-testid="spec-email" /></div>
          <div><Label className="text-slate-300">{L("Password iniziale", "Initial password", "Anfangspasswort")}</Label><Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="mt-1.5 bg-navy border-white/10" data-testid="spec-password" /></div>
          <div>
            <Label className="text-slate-300">{L("Wallet supportati", "Supported wallets", "Unterstützte Wallets")}</Label>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {wallets.map((w) => (
                <button key={w.slug} type="button" onClick={() => toggleWallet(w.slug)} className={`rounded-full border px-2.5 py-1 text-xs ${f.wallet_types.includes(w.slug) ? "border-cyanx text-cyanx bg-cyanx/10" : "border-white/10 text-slate-400"}`}>{w.name}</button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={!f.email || f.password.length < 8} className="bg-gold text-navy hover:bg-gold-hover" data-testid="spec-save">{L("Crea", "Create", "Erstellen")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
